import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

export const THEME_STORAGE_KEY = 'jobseeqr-appearance';
const ThemeContext = createContext(null);

export const readThemePreference = () => {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(readThemePreference);

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme === 'light' ? 'light' : 'normal';
    document.documentElement.style.backgroundColor = theme === 'light' ? '#ffffff' : '#031128';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'light' ? '#ffffff' : '#031128');
  }, [theme]);

  useLayoutEffect(() => {
    const syncPreference = (event) => {
      if (event.key === THEME_STORAGE_KEY || event.key === null) {
        setTheme(readThemePreference());
      }
    };
    window.addEventListener('storage', syncPreference);
    return () => window.removeEventListener('storage', syncPreference);
  }, []);

  const setThemePreference = useCallback((nextTheme) => {
    if (!['dark', 'light'].includes(nextTheme)) return;
    setTheme(nextTheme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
    } catch {
      // The setting still works for this session when browser storage is unavailable.
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemePreference(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setThemePreference]);

  const value = useMemo(() => ({ theme, toggleTheme, setThemePreference }), [theme, toggleTheme, setThemePreference]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};
